import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from './useAppDispatch';
import { fetchGeoFilters } from '../store/slice/filter-slice';
import { setGlobalFilter } from '../store/slice/global-filters';
import { useLocation } from 'react-router-dom';
import { GeoClass } from '../interface/response/GeoFiltersResponse';
import { CASH_REGISTER_URL } from '../util/url';
import { GeoColumns } from '../enum/GeoDivisionColumnName';

/**
 * `useGeoFilter` is a custom React hook for managing cascading geo-filters,
 * where each level depends on the selected options from the previous level.
 *
 * ### Features:
 *
 * - `handleChange(level, selectedItems)`
 *   Calls the API to load options for the next level based on the selected items.
 *   Resets all lower levels.
 *
 * - `handleDropDown(level)`
 *   Loads options when a dropdown is opened, only if the previous level has selected items.
 *
 * - `selectAll(level, select)`
 *   Selects or deselects all options at the given level.
 *   If all are selected, it automatically triggers loading for the next level.
 *
 * ### Behavior:
 *
 * - Selecting items at one level triggers loading of corresponding options at the next level.
 * - Deselecting an item removes all dependent selections in lower levels.
 * - Data is loaded dynamically only when needed (lazy-loading).
 *
 * ### `isDisabled` Logic:
 *
 * A filter column is considered **disabled** if:
 *
 * - It is not the first column **AND**
 * - The previous column has no selected items **OR**
 * - There are no valid options for this column in `allOptsFiltered` **OR**
 * - The previous column is also disabled (checked recursively)
 *
 * Uses memoization to avoid redundant calculations during recursive checks.
 *
 * @returns An object containing methods and state logic for managing geo filters:
 * - `handleChange(level: number, selectedItems: any): void`
 * - `handleDropDown(level: number): void`
 * - `selectAll(level: number, select: boolean): void`
 * - `isDisabled(level: number): boolean`
 */

export const useGeoFilter = (
  filters: any,
  setFilters: (f: GeoClass) => void,
  columns: string[],
  allOptsFiltered: Record<string, any[]>,
) => {
  const dispatch = useAppDispatch();
  const [numberOfSelected, setNumberOfSelected] = useState<any>({});
  const [filteredGeoData, setFilteredGeoData] = useState([]);

  const onFilteredData = useMemo(() => {
    const map: { [key: string]: (filteredData: { ID: string; Name: string }[]) => void } = {};
    columns.forEach((col) => {
      map[col] = (filteredData: { ID: string; Name: string }[]) => {
        setFilteredGeoData((prev: any) => {
          if (JSON.stringify(prev[col]) === JSON.stringify(filteredData)) {
            return prev;
          }

          return {
            ...prev,
            [col]: filteredData,
          };
        });
      };
    });
    return map;
  }, [columns]);

  const isDisabled = useCallback(
    (column: string): boolean => {
      const memo = new Map<string, boolean>();

      const checkDisabled = (col: string): boolean => {
        if (memo.has(col)) return memo.get(col)!;

        const index = columns.indexOf(col);
        if (index === 0) {
          memo.set(col, false);
          return false;
        }

        const prevCol = columns[index - 1];

        if (!filters[prevCol] || filters[prevCol].length === 0) {
          memo.set(col, true);
          return true;
        }

        const result = checkDisabled(prevCol);
        memo.set(col, result);
        return result;
      };

      return checkDisabled(column);
    },
    [columns, filters],
  );

  const location = useLocation();
  const currentPath = location.pathname;

  const handleChange = useCallback(
    (col: string, option: GeoClass) => {
      const columnIndex = columns.indexOf(col);
      const prevFilters = { ...filters };

      let newSelections;

      if (col === GeoColumns.GROUP && currentPath === CASH_REGISTER_URL) {
        // Single-select
        const currentlySelected = filters.Group?.[0];
        if (currentlySelected?.ID === option.ID) {
          newSelections = [];
        } else {
          newSelections = [option];
        }
      } else {
        // Multi-select
        const currentSelections = filters[col] || [];
        const isSelected = currentSelections.some((item: GeoClass) => item.ID === option.ID);
        newSelections = isSelected
          ? currentSelections.filter((item: GeoClass) => item.ID !== option.ID)
          : [...currentSelections, option];
      }

      const newFilters = { ...filters, [col]: newSelections };

      if (newSelections.length === 0) {
        // If everything is deselected in this column, remove all items below
        for (let i = columnIndex + 1; i < columns.length; i++) {
          newFilters[columns[i]] = [];
        }
      } else {
        // Otherwise, clear only the selections that are no longer valid
        for (let i = columnIndex + 1; i < columns.length; i++) {
          const c = columns[i];
          const valid = allOptsFiltered[c] || [];
          const selected = newFilters[c] || [];
          newFilters[c] = selected.filter((sel: GeoClass) => valid.some((opt: GeoClass) => opt.ID === sel.ID));
        }
      }

      setFilters(newFilters);

      dispatch(
        fetchGeoFilters({
          [`${col}ID`]: newSelections.map((f: GeoClass) => f.ID),
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          currentColumn: col,
          currentFilters: newFilters,
          previousFilters: prevFilters,
          currentPath: currentPath,
        }),
      );
    },
    [columns, filters, allOptsFiltered, setFilters, dispatch, currentPath],
  );

  const handleDropdownOpen = useCallback(
    (col: string) => {
      // If the column already has options, do nothing:
      const options = allOptsFiltered[col] || [];
      if (options.length > 0) return;
      // Find the parent column:
      const colIndex = columns.indexOf(col);
      if (colIndex === 0) return;
      const parentCol = columns[colIndex - 1];

      const selected = filters[parentCol] || [];
      if (selected.length === 0) return;

      const newFilters = { ...filters };
      // Clear invalid selections in all columns below the current one:
      for (let nextColIndex = colIndex + 1; nextColIndex < columns.length; nextColIndex++) {
        const nextColumn = columns[nextColIndex];
        const validOptionsForColumn = allOptsFiltered[nextColumn];

        // If there are no valid options for this column, skip it
        if (!validOptionsForColumn || validOptionsForColumn.length === 0) continue;

        // Get currently selected options for this column
        const selectedOptions = newFilters[nextColumn] || [];

        // Keep only those selected options which are still valid
        newFilters[nextColumn] = selectedOptions.filter((selectedOption: GeoClass) =>
          validOptionsForColumn.some((validOption) => validOption.ID === selectedOption.ID),
        );
      }
      setFilters(newFilters);

      dispatch(
        fetchGeoFilters({
          [`${parentCol}ID`]: selected.map((f: GeoClass) => f.ID),
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          currentColumn: col,
          currentFilters: newFilters,
          previousFilters: filters,
        }),
      );
    },
    [columns, filters, allOptsFiltered, setFilters, dispatch],
  );

  const selectAll = useCallback(
    (col: string, options: string[]) => {
      const selected = filters[col] || [];
      const isAllSelected = selected.length === options.length;

      const updated = {
        ...filters,
        [col]: isAllSelected ? [] : options,
      };

      const colIndex = columns.indexOf(col);

      for (let i = colIndex + 1; i < columns.length; i++) {
        updated[columns[i]] = [];
      }

      Object.entries(updated).forEach(([key, value]) => {
        dispatch(setGlobalFilter({ key: key as any, value }));
      });

      dispatch(
        fetchGeoFilters({
          [`${col}ID`]: updated[col].map((f: GeoClass) => f.ID),
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          currentColumn: col,
          currentFilters: updated,
          previousFilters: filters,
        }),
      );
    },
    [columns, filters, dispatch],
  );

  useEffect(() => {
    const newCounts: Record<string, number> = {};

    let changed = false;

    columns.forEach((col) => {
      const selected = filters[col] || [];

      let count = 0;

      count = selected.length;

      if (isDisabled(col)) {
        count = 0;
      }

      newCounts[col] = count;

      if (numberOfSelected[col] !== count) {
        changed = true;
      }
    });

    if (changed) {
      setNumberOfSelected(newCounts);
    }
  }, [filters, isDisabled, columns, numberOfSelected]);

  return {
    handleChange,
    handleDropdownOpen,
    selectAll,
    numberOfSelected,
    filteredGeoData,
    onFilteredData,
    isDisabled,
  };
};
